export function stripHtmlToText(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style\b[^>]*>/gi, ' ')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script\b[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractFromJsonBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const record = body as Record<string, unknown>;

  if (typeof record.message === 'string' && record.message) {
    return record.message;
  }

  if (typeof record.errorMessage === 'string' && record.errorMessage) {
    return record.errorMessage;
  }

  // Assistant service returns { error: string }, unlike the API's { error: [...] } shape.
  if (typeof record.error === 'string' && record.error) {
    return record.error;
  }

  if (Array.isArray(record.error) && record.error.length > 0) {
    const first = record.error[0] as Record<string, unknown>;
    if (typeof first?.message === 'string' && first.message) return first.message;
  }

  return fallback;
}

export async function getResponseErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  const isJson =
    contentType.includes('application/json') || contentType.includes('application/problem+json');

  if (isJson) {
    const body = await response.json().catch(() => null);
    return extractFromJsonBody(body, fallback);
  }

  const raw = await response.text().catch(() => '');
  if (!raw) return fallback;

  const parsed = tryParseJson(raw);
  if (parsed !== null) return extractFromJsonBody(parsed, fallback);

  const looksLikeHtml =
    contentType.includes('html') || /^\s*<(?:!doctype|html|head|body)/i.test(raw);
  return looksLikeHtml ? stripHtmlToText(raw) || fallback : raw;
}

/** Throws an `Error` with the extracted API message when any response failed. */
export async function assertResponseOk(
  responses: Response | Response[],
  fallback: string
): Promise<void> {
  const failed = [responses].flat().find((response) => !response.ok);
  if (failed) throw new Error(await getResponseErrorMessage(failed, fallback));
}

/**
 * Waits for every request to settle before throwing, so callers that refresh on failure
 * see all partial successes.
 */
export async function assertSettledResponsesOk(
  requests: Promise<Response>[],
  fallback: string
): Promise<void> {
  const results = await Promise.allSettled(requests);
  const rejected = results.find((result) => result.status === 'rejected');
  if (rejected) throw rejected.reason;
  await assertResponseOk(
    results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : [])),
    fallback
  );
}

export const toErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;
